import { isObject } from 'util';

import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, catchRenderException, Endpoint, Route, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { FieldAPI, FileUploaderAPI, FormAPI, PanelAPI, TextAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnBeforeSubmit, OnCancel, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import HistoryService from '@philips-emr/tws-core/lib/services/history-service';
import { format } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyObject } from '@philips-emr/tws-utils';

import { FragmentNames } from '../../codegen';
import { CartaoSolicitarAliases as Aliases } from '../../codegen/constants/aliases';
import { Anexo } from '../../interfaces/anexo';
import BaseController from '../BaseController';

interface Retorno {
  valor: string;
  mensagem: string;
}

@Controller({ fragment: FragmentNames.CARTAO_SOLICITAR })
class CartaoSolicitarController extends BaseController {

  private regraAnexos: Anexo[];

  @Inject(Aliases.FIELD_UPLOAD)
  private fieldUpload: FieldAPI;
  @Inject(Aliases.FILE_UPLOADER)
  private fileUploaderAPI: FileUploaderAPI;
  @Inject(Aliases.FORM_SOLICITAR)
  private formSolicitar: FormAPI;
  @Inject(Aliases.PANEL_LOADER_1)
  private panelLoader: PanelAPI;
  @Inject(Aliases.TEXT_MENSAGEM)
  private fieldMensagem: TextAPI;
  @Inject(Aliases.TEXT_MENSAGEM2)
  private textMensagem2: TextAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastForm: ToastNotificationAPI;

  @OnMount(Aliases.LAYOUT_CARTAO_SOLICITAR)
  onMount(): void {
    const { nrSeqBeneficiario } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqBeneficiario });
    this.getPredefinedFiles();
    this.checkStatus();
  }

  @OnAfterFetch(Aliases.FETCH_DADOS)
  onAfterFetch(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const data = event.data as Retorno;
    const { valor, mensagem } = data;

    if (valor && valor !== '0') {
      const cobranca = MessageService.translate('stipulator.acao.implica.cobranca');
      const mensagem = `${cobranca} ${format('currency', valor)}`;

      this.fieldMensagem.setValue(mensagem);
    } else {
      const mensagens = mensagem.split('%%');
      this.fieldMensagem.setValue(MessageService.translate(mensagens[0]));
      this.textMensagem2.setValue(MessageService.translate(mensagens[1]));

    }

  }

  @OnBeforeSubmit(Aliases.FORM_SOLICITAR)
  onBeforeSubmit(): void {
    const dados = this.formSolicitar.getRecord();
    
    dados.anexos = this.handleAnexos(dados.anexos as Anexo[]);

    this.formSolicitar.setRecord(dados);
  }

  @OnSubmit(Aliases.FORM_SOLICITAR)
  onSolicitarClick(): void {
    this.checkStatus();
  }

  @OnCancel(Aliases.FORM_SOLICITAR)
  onCancelForm(): void {
    const { nrSeqGrupoContratoWeb, nrSeqContrato } = HistoryService.getCurrentRouteParams();
    HistoryService.push(APPLICATION_ROUTES.ALTERACAO_BENEFICIARIO, { nrSeqGrupoContratoWeb, nrSeqContrato });
  }

  @OnClose(Aliases.TOAST_NOTIFICATION)
  onCloseToast(): void {
    this.toastForm.setVisible(false);
  }

  private getPredefinedFiles(): void {

    const ieFuncionalidade = '12';
    void HttpService.get(Endpoint.GET.CHECK_TIPO_ANEXOS, { ieFuncionalidade })
      .then(response => {
        this.fieldUpload.setVisible(true);
        const anexos = response.body as Anexo[];

        const predefinedFiles = anexos.map(regras => {
          const { dsLabelCampo: value, nrSeqRegraAnexo: key } = regras;
          return { key: String(key), value };
        });

        this.regraAnexos = anexos;
        catchRenderException(() => this.fileUploaderAPI.setPredefinedFiles(predefinedFiles));

        if (predefinedFiles.length == 0) {
          this.fieldUpload.setVisible(false);
        }
      });
  }

  private solicitar(): void {
    const valid = this.formSolicitar.isValid();

    if (!valid) {
      this.formSolicitar.setState({ forceValidate: true });
      return;
    }

    this.panelLoader.setVisible(true);

    const { nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.CARTAO_SOLICITAR, { nrSeqBeneficiario, nrSeqContrato }, { body: this.formSolicitar.getRecord() })
      .then(response => {
        const mensagem = MessageService.translate('stipulator.aviso-solicitacao-realizada').concat(response.body as string);
        ToastServiceStipulator.success(mensagem);
        this.checkStatus();

        this.panelLoader.setVisible(false);
        this.formSolicitar.setEditing(false);
      }).catch(response => {
        this.panelLoader.setVisible(false);

        const { body } = response;
        const mensagem = MessageService.translate('stipulator.falha').concat(body as string);
        const openToast = (msg: string): void => {
          this.toastForm.setMessage(msg);
          this.toastForm.setVisible(true);

        };

        if (Array.isArray(body)) {
          const data = body as Anexo[];
          const message = `${MessageService.translate('stipulator.obrigatorio-anexar-arquivo')} ${data.map(anexo => anexo.dsLabelCampo).join(', ')}.`;
          return openToast(message);
        }

        if (isObject(body) && !isEmptyObject(body)) {
          return openToast(body[Object.keys(body)[0]]);
        }

        this.formSolicitar.setEditing(true);
        return openToast(mensagem);

      });
  }

  private checkStatus(): void {
    const { nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();

    void HttpService.get(Endpoint.GET.CARTAO_STATUS, { nrSeqBeneficiario, nrSeqContrato })
      .then(response => {
        const status = response.body;

        switch (status) {
          case 'A':
            HistoryService.push(Route.CARTAO_CONSULTAR, { nrSeqBeneficiario });
            break;
          case 'CR':
            HistoryService.push(Route.CARTAO_CONFIRMAR, { nrSeqBeneficiario });
            break;
          default:
            this.solicitar();
            break;
        }
      });
  }

  private handleAnexos(anexos: Anexo[]): Anexo[] {
    const handledAnexos = anexos?.map(anexo => {
      const handledAnexo = { ...anexo };
      const predefinedFileKey = anexo.predefinedFileKey;
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? '0';
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }

}

export default CartaoSolicitarController;
